import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { DispatchRiderSnapshot } from '@/services/api/dispatches';

interface ReassignRiderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stop being moved. Null = modal closed / not yet opened for a stop. */
  stopId: string | null;
  /** Rider currently owning the stop's batch (shown as "Current", can't pick). */
  currentRiderId: number | null;
  riders: DispatchRiderSnapshot[];
  onSubmit: (stopId: string, rider: DispatchRiderSnapshot) => Promise<void>;
}

const ReassignRiderModal: React.FC<ReassignRiderModalProps> = ({
  isOpen,
  onClose,
  stopId,
  currentRiderId,
  riders,
  onSubmit,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedId(currentRiderId);
    }
  }, [isOpen, currentRiderId]);

  const handleSubmit = async () => {
    if (!stopId || selectedId == null) return;
    const rider = riders.find((r) => r.id === selectedId);
    if (!rider) return;
    setSubmitting(true);
    try {
      await onSubmit(stopId, rider);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    selectedId != null &&
    stopId != null &&
    selectedId !== currentRiderId;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-user-shared-line" />}
      iconTone="primary"
      title="Reassign stop"
      subtitle="Move this stop and its items to another rider. The stop merges into that rider's existing trip of the same kind — dropoff or pickup — or starts a new one."
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Reassigning…' : 'Reassign'}
          </Button>
        </div>
      }
    >
      <div className="max-h-72 overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {riders.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            No riders in this dispatch's snapshot.
          </div>
        ) : (
          riders.map((r) => {
            const checked = selectedId === r.id;
            const isCurrent = currentRiderId === r.id;
            return (
              <label
                key={r.id}
                className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  checked
                    ? 'bg-bg-weak-50 border-l-2 border-l-accent'
                    : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                }`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <input
                    type="radio"
                    name="reassign-rider"
                    checked={checked}
                    onChange={() => setSelectedId(r.id)}
                    disabled={submitting}
                    className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                      {r.name}
                    </span>
                    <span className="block font-mono text-[11px] text-text-sub-600 tabular-nums">
                      {r.code}
                    </span>
                  </span>
                </span>
                {isCurrent && (
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-soft-400 shrink-0">
                    Current
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </FormModal>
  );
};

export default ReassignRiderModal;
